import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addMetadataElementsToCanvas as addMetadataElementsToCanvasAction,
  selectIsFunnelMapEmpty,
} from '../../../../reducers/united-canvas/unitedCanvasReducer';
import {
  setIsLoadingCursor,
  setTransientFunnel,
} from '../../../../reducers/canvasPageStateReducer';
import { FunnelType } from '../../../../types/enums/FunnelType';
import { ConfigurationItem } from '../../../documentation/dependencies/DependenciesTypes';
import { ConfigurationType } from '../../../documentation/dependencies/types';
import { useCrmOrgsApiFacade } from '../../../../apis/facades/useCrmOrgsApiFacade';
import { useFetchConfigurationItem } from '../dialogs/dependencies/useFetchConfigurationItem';
import { getFullMetadataElementId } from './metadataElementUtils';
import assignDefaultColorsAndIcons from './metadataElementsColorsAndIcons';
import { useFeatureToggle } from '../../../../feature-flags/useFeatureToggle';
import { telemetry } from '../../../../telemetry';
import { useSweepNotifications } from '../../../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../../../reducers/notificationsReducer';

export type MetadataConnection = {
  crmOrgId: string;
  metadataId: string;
};

export type FunnelMapPosition = {
  column: number;
  row: number;
};

export type FullMetadataElement = {
  name: string;
  type: string;
  crmOrgId: string;
  metadataId: string;
  position?: FunnelMapPosition;
  dependenciesCons?: MetadataConnection[]; // dependencies connections
  usedByCons?: MetadataConnection[]; // used by connections
  color?: string;
  icon?: string;
  parentType: string;
};

export type CanvasConnection = {
  source: string;
  target: string;
  targetType: FunnelType;
};

export type MetadataElementWithoutConnections = Omit<
  FullMetadataElement,
  'dependenciesCons' | 'usedByCons'
>;

// Helper functions for better code organization
const DEFAULT_POSITION: FunnelMapPosition = { column: 0, row: 0 };

/**
 * Adds default position to metadata elements that don't have one
 */
export const addDefaultPositions = (metadataElements: FullMetadataElement[]) => {
  return metadataElements.map((element) => ({
    name: element.name,
    type: element.type,
    crmOrgId: element.crmOrgId,
    metadataId: element.metadataId,
    position: element.position || DEFAULT_POSITION,
    color: element.color,
    icon: element.icon,
  }));
};

/**
 * Creates canvas connections from metadata element relationships
 */
export const createCanvasConnections = (
  metadataElements: FullMetadataElement[],
  canvasFunnelMap: FunnelMap,
): CanvasConnection[] => {
  const allowedConnections = Object.values(canvasFunnelMap.metadataElements).flatMap(
    (metadataElement) => ({
      crmOrgId: metadataElement.crmOrgId,
      metadataId: metadataElement.metadataId,
    }),
  );
  allowedConnections.push(
    ...metadataElements.map((metadataElement) => ({
      crmOrgId: metadataElement.crmOrgId,
      metadataId: metadataElement.metadataId,
    })),
  );

  const connections = metadataElements.flatMap((metadataElement) => {
    const usedByConnections = createUsedByConnections(metadataElement);
    const dependenciesConnections = createDependenciesConnections(metadataElement);
    return [...usedByConnections, ...dependenciesConnections];
  });
  return connections.filter(
    (connection) =>
      allowedConnections.map(getFullMetadataElementId).includes(connection.source) &&
      allowedConnections.map(getFullMetadataElementId).includes(connection.target),
  );
};

/**
 * Creates "used by" connections (other elements that use this element)
 */
const createUsedByConnections = (metadataElement: FullMetadataElement): CanvasConnection[] => {
  return (
    metadataElement.usedByCons?.map((connection) => ({
      source: getFullMetadataElementId(connection),
      target: getFullMetadataElementId(metadataElement),
      targetType: FunnelType.METADATA_ELEMENT,
    })) ?? []
  );
};

/**
 * Creates dependency connections (elements this element depends on)
 */
const createDependenciesConnections = (
  metadataElement: FullMetadataElement,
): CanvasConnection[] => {
  return (
    metadataElement.dependenciesCons?.map((connection) => ({
      source: getFullMetadataElementId(metadataElement),
      target: getFullMetadataElementId(connection),
      targetType: FunnelType.METADATA_ELEMENT,
    })) ?? []
  );
};

/**
 * Filters out invalid metadata IDs
 */
const isValidMetadataId = (metadataId: string, currentMetadataId: string): boolean => {
  return metadataId !== 'MissingInternalId' && metadataId !== currentMetadataId;
};

/**
 * Converts API response to metadata connections
 */
const convertToMetadataConnections = (
  apiResponse: Record<string, any[]> | undefined,
  crmOrgId: string,
  currentMetadataId: string,
): MetadataConnection[] => {
  if (!apiResponse) {
    return [];
  }
  return (Object.values(apiResponse).flat() ?? [])
    .filter(Boolean)
    .filter(({ metadataId }) => isValidMetadataId(metadataId, currentMetadataId))
    .map(({ metadataId }) => ({
      crmOrgId,
      metadataId,
    }));
};

export const useAddMetadataElementToCanvas = (skipLocating?: boolean) => {
  const dispatch = useDispatch();
  const isFunnelMapEmpty = useSelector(selectIsFunnelMapEmpty);

  const addMetadataElementsToCanvas = useCallback(
    (metadataElements: FullMetadataElement[], canvasFunnelMap: FunnelMap) => {
      assignDefaultColorsAndIcons(metadataElements);

      // Add default positions to elements that don't have one
      const metadataElementsWithPosition = addDefaultPositions(metadataElements);

      // Create canvas connections from metadata relationships
      const connections = createCanvasConnections(metadataElements, canvasFunnelMap);

      // Determine the appropriate action based on canvas state
      // By product definition, first metadata element should be dropped automatically on canvas
      const action =
        isFunnelMapEmpty || skipLocating
          ? addMetadataElementsToCanvasAction({
              metadataElements: metadataElementsWithPosition,
              connections,
            })
          : setTransientFunnel({
              type: FunnelType.METADATA_ELEMENT,
              importType: 'import',
              data: {
                metadataElements: metadataElementsWithPosition,
                connections,
              },
              isPlacingFunnel: true,
            });

      dispatch(action);
    },
    [dispatch, isFunnelMapEmpty, skipLocating],
  );

  const {
    get_crmOrgConfigurationDependenciesFromSalto,
    get_crmOrgConfigurationConsumersFromSalto,
    get_crmOrgFieldConsumerFromSalto,
  } = useCrmOrgsApiFacade();
  const { fetchConfigurationItem } = useFetchConfigurationItem();

  /**
   * Fetches configuration items for all metadata elements
   */
  const fetchConfigurationItems = useCallback(
    async (
      metadataElements: MetadataElementWithoutConnections[],
    ): Promise<Record<string, ConfigurationItem>> => {
      const configurationItems = await Promise.all(
        metadataElements.map(async (metadataElement) => {
          const configurationItem = await fetchConfigurationItem(
            metadataElement.metadataId,
            metadataElement.crmOrgId,
          );
          return [getFullMetadataElementId(metadataElement), configurationItem] as const;
        }),
      );
      return Object.fromEntries(configurationItems);
    },
    [fetchConfigurationItem],
  );

  /**
   * Fetches dependencies and consumers for a single metadata element
   */
  const fetchElementConnections = useCallback(
    async (
      metadataElement: MetadataElementWithoutConnections,
      configurationItem: ConfigurationItem,
    ) => {
      const dependenciesCall = () => {
        return get_crmOrgConfigurationDependenciesFromSalto({
          crmOrgId: metadataElement.crmOrgId,
          configurationKey: configurationItem.metadataId,
          configurationName: configurationItem.name,
          objectName: configurationItem.objectName ?? '',
          filename: configurationItem.filename,
        });
      };

      const consumersCall = () => {
        return configurationItem.parentType === ConfigurationType.fields
          ? get_crmOrgFieldConsumerFromSalto({
              crmOrgId: metadataElement.crmOrgId,
              objectName: configurationItem.objectName ?? '',
              fieldName: configurationItem.name ?? '',
              filename: configurationItem.filename,
            })
          : get_crmOrgConfigurationConsumersFromSalto({
              crmOrgId: metadataElement.crmOrgId,
              configurationKey: configurationItem.metadataId,
              configurationName: configurationItem.name,
              objectName: configurationItem.objectName ?? '',
              filename: configurationItem.filename,
            });
      };

      const [dependencies, consumers] = await Promise.all([dependenciesCall(), consumersCall()]);

      return { dependencies, consumers };
    },
    [
      get_crmOrgConfigurationDependenciesFromSalto,
      get_crmOrgConfigurationConsumersFromSalto,
      get_crmOrgFieldConsumerFromSalto,
    ],
  );

  /**
   * Enriches a single metadata element with its connections
   */
  const enrichMetadataElementWithConnections = useCallback(
    async (
      metadataElement: MetadataElementWithoutConnections,
      configurationItems: Record<string, ConfigurationItem>,
      parentElement?: {
        metadataId: string;
        crmOrgId: string;
      },
    ): Promise<FullMetadataElement> => {
      const configurationItem = configurationItems[getFullMetadataElementId(metadataElement)];
      if (!configurationItem) {
        return metadataElement;
      }
      const { dependencies, consumers } = await fetchElementConnections(
        metadataElement,
        configurationItem,
      );

      const dependenciesConnections = convertToMetadataConnections(
        dependencies,
        metadataElement.crmOrgId,
        configurationItem.metadataId,
      );

      const consumersConnections = convertToMetadataConnections(
        consumers,
        metadataElement.crmOrgId,
        configurationItem.metadataId,
      );
      if (parentElement) {
        consumersConnections.push({
          crmOrgId: parentElement.crmOrgId,
          metadataId: parentElement.metadataId,
        });
      }

      return {
        ...metadataElement,
        dependenciesCons: dependenciesConnections,
        usedByCons: consumersConnections,
      };
    },
    [fetchElementConnections],
  );

  const { metadataElementsConnections } = useFeatureToggle();
  const { addNotification } = useSweepNotifications();

  const getMetadataElementsWithConnections = useCallback(
    async (
      metadataElements: MetadataElementWithoutConnections[],
      parentElement?: {
        metadataId: string;
        crmOrgId: string;
      },
    ) => {
      // // Fetch configuration items for all metadata elements
      const configurationItems = await fetchConfigurationItems(metadataElements);
      // Enrich each metadata element with its connections
      const metadataElementsWithConnections = await Promise.all(
        metadataElements.map((metadataElement) =>
          enrichMetadataElementWithConnections(metadataElement, configurationItems, parentElement),
        ),
      );
      return metadataElementsWithConnections;
    },
    [enrichMetadataElementWithConnections, fetchConfigurationItems],
  );

  const addMetadataElementsToCanvasAndEnrichConnections = useCallback(
    async ({
      metadataElements,
      canvasFunnelMap,
      parentElement,
    }: {
      metadataElements: MetadataElementWithoutConnections[];
      canvasFunnelMap: FunnelMap;
      parentElement?: {
        metadataId: string;
        crmOrgId: string;
      };
    }) => {
      dispatch(setIsLoadingCursor(true));
      try {
        if (!metadataElementsConnections) {
          addMetadataElementsToCanvas(metadataElements, canvasFunnelMap);
          return;
        }
        const metadataElementsWithConnections = await getMetadataElementsWithConnections(
          metadataElements,
          parentElement,
        );

        // Add the enriched elements to the canvas
        addMetadataElementsToCanvas(metadataElementsWithConnections, canvasFunnelMap);
      } catch (error) {
        telemetry.captureError(error);
        addNotification({
          message: 'Failed to add metadata elements to canvas',
          variant: SweepNotificationVariant.Error,
        });
      } finally {
        dispatch(setIsLoadingCursor(false));
      }
    },
    [
      addMetadataElementsToCanvas,
      addNotification,
      dispatch,
      getMetadataElementsWithConnections,
      metadataElementsConnections,
    ],
  );

  return {
    addMetadataElementsToCanvasAndEnrichConnections,
    getMetadataElementsWithConnections,
  };
};
